import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Copy,
  CheckCircle,
  ArrowRight,
  Building2,
  Hash,
  Clock,
  Shield,
  Share2
} from 'lucide-react';
import Button from '../../components/ui/Button';
import PageHeader from '../../components/ui/PageHeader';
import { useApp, getBrokerBankAccount } from '../../context/AppContext';
import { useBroker } from '../../context/BrokerContext';
import './ReferenceNumber.css';

function ReferenceNumber() {
  const navigate = useNavigate();
  const { currentTransaction, referenceNumber, generateReferenceNumber } = useApp();
  const { brokerId, brokerName, refPrefix, getBrokerPath } = useBroker();

  // Get broker-specific bank account
  const bankAccount = getBrokerBankAccount(brokerId);

  const [copied, setCopied] = useState({});
  const [refNumber, setRefNumber] = useState(referenceNumber);

  // Generate reference number if not exists
  useEffect(() => {
    const generateRef = async () => {
      if (!refNumber && currentTransaction) {
        const newRef = await generateReferenceNumber(refPrefix);
        setRefNumber(newRef);
      }
    };
    generateRef();
  }, [refNumber, currentTransaction, generateReferenceNumber, refPrefix]);

  // Redirect if no transaction
  useEffect(() => {
    if (!currentTransaction) {
      navigate(getBrokerPath('/'));
    }
  }, [currentTransaction, navigate, getBrokerPath]);

  const handleCopy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied({ ...copied, [field]: true });
      setTimeout(() => {
        setCopied({ ...copied, [field]: false });
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleShare = async () => {
    const shareText = `${brokerName} Payment Details\n\nTransaction ID: ${refNumber}\nBank: ${bankAccount.bankName}\nAccount Title: ${bankAccount.accountTitle}\nAccount Number: ${bankAccount.accountNumber}\nIBAN: ${bankAccount.iban}\nAmount: PKR ${currentTransaction?.amount?.toLocaleString() || '0'}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${brokerName} Payment Details`,
          text: shareText,
        });
      } catch (err) {
        console.error('Share failed:', err);
      }
    } else {
      handleCopy(shareText, 'all');
    }
  };

  const handleContinue = () => {
    navigate(getBrokerPath('/receipt-upload'));
  };

  if (!currentTransaction) {
    return null;
  }

  return (
    <div className="reference-page animate-fade-in">
      {/* Unified Main Card */}
      <div className="reference-main-card">
        {/* Header with user info */}
        <PageHeader showBackButton={false} greeting="Complete your payment">
          {/* Transaction ID Display */}
          <div className="ref-display-section">
            <div className="ref-badge">
              <Hash size={14} />
              <span>Your Transaction ID</span>
            </div>
            <div className="ref-number-box">
              <span className="ref-number">{refNumber || 'Generating...'}</span>
              {refNumber && (
                <button
                  className={`copy-btn-ref ${copied.reference ? 'copied' : ''}`}
                  onClick={() => handleCopy(refNumber, 'reference')}
                >
                  {copied.reference ? <CheckCircle size={18} /> : <Copy size={18} />}
                </button>
              )}
            </div>
            <p className="ref-note">Save this ID to track your transaction status</p>
          </div>
        </PageHeader>

        {/* Content Area */}
        <div className="reference-content">
          {/* Bank Details Section */}
          <div className="section-header">
            <Building2 size={18} />
            <span>Transfer to This Account</span>
          </div>

          <div className="bank-details-container">
            <div className="bank-row">
              <span className="bank-label">Bank Name</span>
              <div className="bank-value-wrap">
                <span className="bank-value">{bankAccount.bankName}</span>
                <button
                  className={`copy-btn-sm ${copied.bank ? 'copied' : ''}`}
                  onClick={() => handleCopy(bankAccount.bankName, 'bank')}
                >
                  {copied.bank ? <CheckCircle size={14} /> : <Copy size={14} />}
                </button>
              </div>
            </div>

            <div className="bank-row">
              <span className="bank-label">Account Title</span>
              <div className="bank-value-wrap">
                <span className="bank-value">{bankAccount.accountTitle}</span>
                <button
                  className={`copy-btn-sm ${copied.title ? 'copied' : ''}`}
                  onClick={() => handleCopy(bankAccount.accountTitle, 'title')}
                >
                  {copied.title ? <CheckCircle size={14} /> : <Copy size={14} />}
                </button>
              </div>
            </div>

            <div className="bank-row">
              <span className="bank-label">Account Number</span>
              <div className="bank-value-wrap">
                <span className="bank-value mono">{bankAccount.accountNumber}</span>
                <button
                  className={`copy-btn-sm ${copied.account ? 'copied' : ''}`}
                  onClick={() => handleCopy(bankAccount.accountNumber, 'account')}
                >
                  {copied.account ? <CheckCircle size={14} /> : <Copy size={14} />}
                </button>
              </div>
            </div>

            <div className="bank-row no-border">
              <span className="bank-label">IBAN</span>
              <div className="bank-value-wrap">
                <span className="bank-value mono iban">{bankAccount.iban}</span>
                <button
                  className={`copy-btn-sm ${copied.iban ? 'copied' : ''}`}
                  onClick={() => handleCopy(bankAccount.iban, 'iban')}
                >
                  {copied.iban ? <CheckCircle size={14} /> : <Copy size={14} />}
                </button>
              </div>
            </div>
          </div>

          {/* Share Button */}
          <button className="share-all-btn" onClick={handleShare}>
            <Share2 size={16} />
            <span>Share All Payment Details</span>
          </button>

          {/* Timer Notice */}
          <div className="timer-notice">
            <Clock size={18} />
            <div className="timer-text">
              <strong>Complete within 30 minutes</strong>
              <span>Please complete your payment soon</span>
            </div>
          </div>

          {/* Continue Button */}
          <Button
            variant="primary"
            size="large"
            fullWidth
            icon={ArrowRight}
            iconPosition="right"
            onClick={handleContinue}
            className="continue-btn"
          >
            I've Made the Payment
          </Button>
        </div>

        {/* Footer */}
        <div className="reference-footer">
          <Shield size={16} />
          <span>Only send from your whitelisted account</span>
        </div>
      </div>
    </div>
  );
}

export default ReferenceNumber;
